// thermalHeatmapSubsystemConfig.ts

import type { SubsystemType, SubsystemDeck } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/subsystems";
import { SUBSYSTEM_LAYOUT, DECK_TO_NODE_PREFIX } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/subsystems";

/**
 * Configuration for subsystem placement on thermal heatmap
 */

export interface ThermalHeatmapSubsystemPlacement {
  type: SubsystemType;
  label: string;
  deck: SubsystemDeck;
  nodeId: string;
  shape?: "box" | "cylinder";  // Geometry shape (defaults to "box")
  rotation?: { x?: number; y?: number; z?: number }; // Rotation in degrees
  positionOffset?: { x?: number; y?: number; z?: number }; // Offset in cm
  dimensionOverride?: { width?: number; height?: number; length?: number }; // Override template dimensions (cm)
}

/**
 * Heatmap-specific rotation/positionOffset/shape/dimensionOverride overrides for subsystems, keyed by label.
 * Merged with SUBSYSTEM_LAYOUT identity data to produce THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS.
 * Note: rotation/positionOffset differ from Designer because the heatmap uses a flattened 2D-grid-on-face view.
 */
interface HeatmapPlacementOverride {
  shape?: "box" | "cylinder";
  rotation?: { x?: number; y?: number; z?: number };
  positionOffset?: { x?: number; y?: number; z?: number };
  dimensionOverride?: { width?: number; height?: number; length?: number };
}

const HEATMAP_PLACEMENT_OVERRIDES: Record<string, HeatmapPlacementOverride> = {
  "X-Band Module #1": { positionOffset: { x: -40, y: -40, z: 20 } },
  "X-Band Module #2": { positionOffset: { x: 40, y: -40, z: 20 } },
  "S-Band Module #1": { positionOffset: { x: 0, y: -40, z: 0 } },
  "S-Band Module #2": { positionOffset: { x: 0, y: -40, z: 0 } },
  "Reaction Wheel #1": { rotation: { x: 0, y: 0, z: 45 }, positionOffset: { x: 0, y: 30, z: 0 } },
  "Reaction Wheel #2": { rotation: { x: 0, y: 0, z: 135 }, positionOffset: { x: 0, y: 30, z: 0 } },
  "Reaction Wheel #3": { rotation: { x: 0, y: 0, z: 225 }, positionOffset: { x: 0, y: 30, z: 0 } },
  "Reaction Wheel #4": { rotation: { x: 0, y: 0, z: 315 }, positionOffset: { x: 0, y: 30, z: 0 } },
  "Star Tracker #1": { rotation: { x: 0, y: 0, z: 0 }, positionOffset: { x: 90, y: 5, z: -80 } },
  "Star Tracker #2": { rotation: { x: 0, y: 0, z: 0 }, positionOffset: { x: -90, y: 5, z: -80 } },
  "Avionics": { positionOffset: { x: 0, y: 35, z: 0 } },
  "Power System": { positionOffset: { x: -20, y: 45, z: 0 } },
  "Battery Pack": { positionOffset: { x: -60, y: 35, z: 0 } },
  "Flexcore": { positionOffset: { x: 0, y: 15, z: 0 } },
  "AIS SDR": { positionOffset: { x: -20, y: 45, z: -20 } },
  "Propulsion Tank": { shape: "cylinder", rotation: { x: 90, y: 0, z: 90 }, positionOffset: { x: -30, y: 25, z: 0 } },
  "Thruster": { shape: "cylinder", rotation: { x: 0, y: 0, z: 0 }, positionOffset: { x: 0, y: -15, z: -80 }, dimensionOverride: { length: 150 } },
  "TCU": { positionOffset: { x: -30, y: 10, z: 0 } },
  "PMS": { positionOffset: { x: 50, y: 10, z: 20 } },
  "IDRSS SDR": { positionOffset: { x: 0, y: -30, z: 0 } },
};

/**
 * Default subsystem placements for thermal heatmap visualization, derived from SUBSYSTEM_LAYOUT
 * plus heatmap-specific overrides. Used as fallback when live thermalModel data is unavailable.
 */
export const THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS: ThermalHeatmapSubsystemPlacement[] = SUBSYSTEM_LAYOUT.map((entry) => ({
  ...entry,
  ...HEATMAP_PLACEMENT_OVERRIDES[entry.label],
}));

/**
 * Subsystem labels keyed by heatmap node ID (e.g. "PX_N9" → "Thruster, TCU").
 * Built once from the placements so hover handlers do a plain lookup.
 */
export const SUBSYSTEM_LABELS_BY_NODE: Record<string, string> = THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS.reduce(
  (map, placement) => {
    const nodeId = `${DECK_TO_NODE_PREFIX[placement.deck]}_N${parseInt(placement.nodeId.split('-')[1], 10) + 1}`;
    map[nodeId] = map[nodeId] ? `${map[nodeId]}, ${placement.label}` : placement.label;
    return map;
  },
  {} as Record<string, string>
);

export const getSubsystemOptionId = (label: string, index?: number) => {
  const normalized = label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  // Include index to ensure uniqueness when similar names exist (e.g., "Star Tracker" vs "Star-Tracker")
  return index !== undefined ? `${normalized}_${index}` : normalized;
};

export const SUBSYSTEM_VISIBILITY_OPTIONS = THERMAL_HEATMAP_SUBSYSTEM_PLACEMENTS.map((p, idx) => ({
  id: getSubsystemOptionId(p.label, idx),
  label: p.label,
}));

// Map from subsystem name to option ID for quick lookups
export const SUBSYSTEM_NAME_TO_OPTION_ID = SUBSYSTEM_VISIBILITY_OPTIONS.reduce(
  (map, option) => {
    map[option.label] = option.id;
    return map;
  },
  {} as Record<string, string>
);
